import { useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ArrowRight, Info } from 'lucide-react'
import { useVehiclesQuery } from '../../hooks/useVehicleQueries'
import { BANKS, DEFAULT_BANK } from '../../data/banks'
import { nameOf } from '../../data/vehicles'
import { animateNumber } from '../../animations/gsapAnimations'
import { pkr, shortPrice } from '../../utils/format'
import {
  MAX_TENURE_YEARS,
  MIN_TENURE_YEARS,
  clamp,
  quote,
} from '../../utils/lease'
import Button from '../ui/Button'
import Select from '../ui/Select'

type LeaseCalculatorProps = {
  /** `compact` is the three-control version embedded in other pages. */
  variant?: 'full' | 'compact'
  /** Seeds the price — a vehicle page passes the car the visitor is looking at. */
  initialPrice?: number
  /** Seeds the deposit, in rupees. A vehicle page passes the car's own — the
      calculator has to open on the same terms the page above it just quoted,
      or the monthly figure changes as the visitor scrolls. Left out, it
      opens on thirty per cent of the price. */
  initialDownPayment?: number
  /** Shown above the heading when a specific car is being quoted. */
  vehicleName?: string
  /** The car picker only makes sense when no car is already in context. */
  showVehiclePicker?: boolean
  className?: string
}

const TENURES = [1, 2, 3, 4, 5, 6, 7] as const

/* A money field has to accept what people type — spaces, commas, a pasted
   "PKR 8,449,000" — and show it back grouped. Digits in, digits out. */
const digitsOf = (value: string) => {
  const digits = value.replace(/\D/g, '')
  return digits ? Number(digits) : 0
}

function MoneyField({
  id,
  label,
  value,
  onChange,
  hint,
  disabled = false,
}: {
  id: string
  label: string
  value: number
  onChange: (value: number) => void
  hint?: string
  disabled?: boolean
}) {
  return (
    <div className="flex flex-col gap-2">
      <label
        htmlFor={id}
        className="label-sm text-steel"
      >
        {label}
      </label>
      <div className="flex items-baseline gap-2 border-b border-line pb-2.5 focus-within:border-lime">
        <span className="text-[12px] text-dim">PKR</span>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          disabled={disabled}
          value={value ? pkr.format(value) : ''}
          placeholder="0"
          onChange={(event) => onChange(digitsOf(event.target.value))}
          className="w-full min-w-0 bg-transparent font-display text-xl font-semibold text-platinum tabular-nums focus:outline-none disabled:text-steel sm:text-2xl"
        />
      </div>
      {hint ? <p className="text-[12px] text-dim">{hint}</p> : null}
    </div>
  )
}

function LeaseCalculator({
  variant = 'full',
  initialPrice,
  initialDownPayment,
  vehicleName,
  showVehiclePicker,
  className = '',
}: LeaseCalculatorProps) {
  const compact = variant === 'compact'
  const withPicker = showVehiclePicker ?? (!initialPrice && !compact)

  /* The picker is the only reason this component needs the catalogue, so a
     compact calculator seeded with one car's price never asks for it. The
     floor it offers is the one a visitor can buy: quoting a lease on a car
     that has gone is a conversation nobody wants to have. */
  const { data: vehicles = [] } = useVehiclesQuery({ unsold: true }, 'featured', withPicker)

  /* The price is seeded once, at mount. A caller whose price arrives
     asynchronously should render this only after its data has loaded, or pass
     a `key`, rather than have it fight the visitor for the field. */
  const [price, setPrice] = useState(initialPrice ?? 8449000)
  const [downPayment, setDownPayment] = useState(
    initialDownPayment ?? Math.round((initialPrice ?? 8449000) * 0.3),
  )
  const [years, setYears] = useState(5)
  const [bankId, setBankId] = useState(DEFAULT_BANK.id)
  const [markupPercent, setMarkupPercent] = useState(DEFAULT_BANK.apr * 100)
  const [carId, setCarId] = useState('')

  const bank = BANKS.find((item) => item.id === bankId) ?? DEFAULT_BANK

  const result = useMemo(
    () =>
      quote({
        price,
        downPayment,
        years,
        markupRate: markupPercent / 100,
      }),
    [price, downPayment, years, markupPercent],
  )

  /* The down payment is stored in rupees and edited either way round, so the
     percentage is derived on the way out and converted on the way in. */
  const downPercent = Math.round(result.downRate * 100)
  const belowBankMinimum = result.downRate < bank.minDownRate - 0.0001
  const overBankTenure = years > bank.maxTenureYears

  const setPriceKeepingRate = (next: number) => {
    const rate = price > 0 ? downPayment / price : 0.3
    setPrice(next)
    setDownPayment(Math.round(next * rate))
  }

  const selectBank = (id: string) => {
    const chosen = BANKS.find((item) => item.id === id) ?? DEFAULT_BANK
    setBankId(chosen.id)
    setMarkupPercent(Number((chosen.apr * 100).toFixed(2)))
  }

  const chooseCar = (id: string) => {
    setCarId(id)
    const car = vehicles.find((item) => item.id === id)
    if (car) setPriceKeepingRate(car.price)
  }

  /* GSAP owns this element's text so the figure can count between values.
     React renders the same number into an sr-only node beside it, because a
     screen reader should hear the settled figure, not sixty intermediate ones. */
  const monthlyRef = useRef<HTMLSpanElement>(null)
  const previousMonthly = useRef(result.monthly)

  useLayoutEffect(() => {
    const el = monthlyRef.current
    if (!el) return

    animateNumber(el, previousMonthly.current, result.monthly, (value) =>
      pkr.format(Math.round(value)),
    )
    previousMonthly.current = result.monthly
  }, [result.monthly])

  const summaryRows = [
    ['Vehicle price', `PKR ${pkr.format(result.price)}`],
    ['Down payment', `PKR ${pkr.format(result.downPayment)}`],
    ['Financing amount', `PKR ${pkr.format(result.financed)}`],
    ['Tenure', `${years} ${years === 1 ? 'year' : 'years'}`],
    ['Markup', `${markupPercent.toFixed(2)}% per year`],
    ['Total markup', `PKR ${pkr.format(result.totalMarkup)}`],
    ['Total payable', `PKR ${pkr.format(result.totalPayable)}`],
  ] as const

  const controls = (
    <div className={`flex flex-col ${compact ? 'gap-6' : 'gap-8'}`}>
      {withPicker && vehicles.length > 0 ? (
        <Select
          label="Choose a car"
          placeholder="Enter a price myself"
          value={carId}
          onChange={chooseCar}
          options={[
            { value: '', label: 'Enter a price myself' },
            ...vehicles.map((car) => ({
              value: car.id,
              label: nameOf(car),
            })),
          ]}
        />
      ) : null}

      <MoneyField
        id="calc-price"
        label="Vehicle price"
        value={price}
        onChange={setPriceKeepingRate}
        hint={price > 0 ? shortPrice(price) : undefined}
      />

      <div className="grid gap-5 sm:grid-cols-[1fr_auto]">
        <MoneyField
          id="calc-down"
          label="Down payment"
          value={downPayment}
          onChange={(value) => setDownPayment(clamp(value, 0, price))}
        />

        <div className="flex flex-col gap-2">
          <label
            htmlFor="calc-down-percent"
            className="label-sm text-steel"
          >
            Percent
          </label>
          <div className="flex items-baseline gap-1 border-b border-line pb-2.5 focus-within:border-lime">
            <input
              id="calc-down-percent"
              type="number"
              inputMode="numeric"
              min={0}
              max={100}
              value={downPercent}
              onChange={(event) =>
                setDownPayment(
                  Math.round(
                    (clamp(Number(event.target.value), 0, 100) / 100) * price,
                  ),
                )
              }
              className="w-16 bg-transparent font-display text-xl font-semibold text-platinum tabular-nums focus:outline-none sm:text-2xl [&::-webkit-inner-spin-button]:appearance-none"
            />
            <span className="text-[12px] text-dim">%</span>
          </div>
        </div>
      </div>

      <fieldset>
        <legend className="label-sm text-steel">
          Tenure
        </legend>
        <div className="mt-3 flex flex-wrap gap-2">
          {TENURES.filter(
            (tenure) => tenure >= MIN_TENURE_YEARS && tenure <= MAX_TENURE_YEARS,
          ).map((tenure) => (
            <button
              key={tenure}
              type="button"
              onClick={() => setYears(tenure)}
              aria-pressed={years === tenure}
              className={`min-h-11 min-w-14 rounded-full border px-4 text-[13px] font-medium transition duration-200 ${
                years === tenure
                  ? 'border-lime bg-lime-ink text-platinum'
                  : 'border-line bg-raised text-steel hover:border-lime/40 hover:text-platinum'
              }`}
            >
              {tenure}y
            </button>
          ))}
        </div>
      </fieldset>

      {!compact ? (
        <>
          <Select
            label="Bank"
            value={bankId}
            onChange={selectBank}
            options={BANKS.map((option) => ({
              value: option.id,
              label: option.name,
            }))}
          />

          <div className="flex flex-col gap-2">
            <div className="flex items-baseline justify-between gap-4">
              <label
                htmlFor="calc-markup"
                className="label-sm text-steel"
              >
                Markup rate
              </label>
              <output
                htmlFor="calc-markup"
                className="text-[13.5px] font-medium text-platinum tabular-nums"
              >
                {markupPercent.toFixed(2)}%
              </output>
            </div>
            <input
              id="calc-markup"
              type="range"
              min={0}
              max={30}
              step={0.25}
              value={markupPercent}
              onChange={(event) => setMarkupPercent(Number(event.target.value))}
              style={{ '--fill': `${(markupPercent / 30) * 100}%` } as React.CSSProperties}
              className="range-lime"
            />
            <p className="text-[12px] text-dim">
              Pre-filled from {bank.name}. Move it to model a rate you have been
              quoted elsewhere.
            </p>
          </div>
        </>
      ) : null}

      {/* Only ever shown when it is true, so it reads as information rather
          than a permanent warning the eye learns to skip. */}
      {(belowBankMinimum || overBankTenure) && !compact ? (
        <p className="flex items-start gap-2.5 rounded-2xl border border-lime/40 bg-lime-ink/40 p-4 text-[13px] leading-relaxed text-silver">
          <Info
            aria-hidden="true"
            strokeWidth={1.7}
            className="mt-0.5 size-4 shrink-0 text-lime-deep"
          />
          <span>
            {belowBankMinimum
              ? `${bank.name} asks for at least ${Math.round(bank.minDownRate * 100)}% down. `
              : ''}
            {overBankTenure
              ? `${bank.name} finances up to ${bank.maxTenureYears} years. `
              : ''}
            We can still put the file in — another partner may take these terms.
          </span>
        </p>
      ) : null}
    </div>
  )

  const output = (
    <div
      className={`flex flex-col rounded-xl border border-line bg-raised p-7 shadow-lift ${
        compact ? '' : 'sm:p-9'
      }`}
    >
      <p className="label-sm text-lime-deep">
        Estimated monthly payment
      </p>

      <p className="mt-3 flex items-baseline gap-2 font-display text-[clamp(2.4rem,6vw,3.8rem)] leading-none font-semibold tracking-[-0.045em] text-platinum tabular-nums">
        <span ref={monthlyRef} aria-hidden="true">
          {pkr.format(result.monthly)}
        </span>
        <span className="font-sans text-base font-medium tracking-normal text-steel">
          PKR/mo
        </span>
      </p>
      <span className="sr-only" aria-live="polite">
        Estimated monthly payment {pkr.format(result.monthly)} rupees
      </span>

      {compact ? (
        <dl className="mt-6 flex flex-col gap-2.5 border-t border-line/70 pt-5">
          {summaryRows.slice(0, 3).map(([term, value]) => (
            <div key={term} className="flex items-baseline justify-between gap-4">
              <dt className="text-[13px] text-steel">{term}</dt>
              <dd className="text-[13px] text-silver tabular-nums">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      ) : (
        <dl className="mt-8 flex flex-col gap-px overflow-hidden rounded-2xl border border-line bg-line/60">
          {summaryRows.map(([term, value]) => (
            <div
              key={term}
              className="flex items-baseline justify-between gap-4 bg-raised px-5 py-3.5"
            >
              <dt className="text-[13.5px] text-steel">{term}</dt>
              <dd className="text-[13.5px] font-medium text-silver tabular-nums">
                {value}
              </dd>
            </div>
          ))}
        </dl>
      )}

      <div className="mt-7 flex flex-col gap-3">
        <Button to="/financing" size="lg" arrow className="w-full">
          Apply for financing
        </Button>
        {compact ? (
          <Button variant="ghost" to="/lease-calculator" className="w-full">
            Open full calculator
          </Button>
        ) : null}
      </div>

      <p className="mt-5 text-[12px] leading-relaxed text-dim">
        Indicative only. Excludes insurance, registration and processing fees,
        which we itemise in your written quote.
      </p>
    </div>
  )

  if (compact) {
    return (
      <div className={`grid gap-8 lg:grid-cols-2 lg:items-start ${className}`}>
        {vehicleName ? (
          <p className="label text-dim lg:col-span-2">
            Quoting {vehicleName}
          </p>
        ) : null}
        {controls}
        {output}
      </div>
    )
  }

  return (
    <section
      id="calculator"
      aria-labelledby="calculator-title"
      className={`border-y border-line band-surface py-24 sm:py-32 ${className}`}
    >
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="max-w-2xl">
          <p className="label text-lime">
            Lease calculator
          </p>
          <h2
            id="calculator-title"
            className="mt-5 font-display text-display-2 text-platinum"
          >
            Know the monthly number first
          </h2>
          <p className="mt-5 text-[15.5px] leading-relaxed text-steel">
            Set the car, the down payment and the bank. Every assumption behind
            the figure is listed beside it — nothing is buried in a footnote.
          </p>
        </div>

        <div className="mt-14 grid gap-10 lg:grid-cols-[1fr_460px] lg:gap-16">
          {controls}
          <div className="lg:sticky lg:top-[100px]">{output}</div>
        </div>

        <p className="mt-10 flex items-center gap-2 text-[13px] text-dim">
          <ArrowRight aria-hidden="true" strokeWidth={1.7} className="size-4" />
          Ready to move? We put one file in front of every partner bank and come
          back with all of their numbers.
        </p>
      </div>
    </section>
  )
}

export default LeaseCalculator
